import style from './SelectPathStorage.module.css';

import arrowSVG from 'assets/svg/arrow-01.svg';

import { PUBLIC_ASSETS, address_server_short } from 'config';
import useOutsideClick from 'hooks/useOutsideClick';
import { useEffect, useRef, useState } from 'react';
import { getStorageChildren, getStoragesV2 } from 'services/storage.services';
import { TreeStore } from 'store/mobx/tree.store';
import Icon from 'ui/Icon';
import Select from 'ui/Select';
import Tree from 'widgets/Tree';
import { v4 as uuid } from 'uuid';

/**
 * Компонент выбора пути хранения на основе древовидной структуры.
 *
 * Использует `Tree` с MobX-хранилищем `treeStorageSelectStore` для отображения и выбора узлов склада.
 *
 * @component
 * @example
 * // Использование внутри формы
 * const [selectedNode, setSelectedNode] = useState(null);
 *
 * <SelectPathStorage
 *   node={selectedNode}
 *   setNode={(node) => setSelectedNode(node)}
 * />
 *
 * @param {Object} props
 * @param {Object} [props.node] - Выбранный узел. Используется для отображения выбранного пути.
 * @param {(node: Object) => void} [props.setNode] - Функция для установки выбранного узла.
 *
 * @returns {JSX.Element} Компонент выбора пути хранения.
 */
const SelectPathStorage = ({
  node = '',
  line = '',
  column = '',
  setNode = undefined,
  setLine = undefined,
  setColumn = undefined,
  withoutPath = false,
  filterFunction = undefined,
}) => {
  const [keyStore] = useState(uuid());
  const [treeStorageSelectStore] = useState(new TreeStore(`STORAGE_SELECT_TREE${keyStore}`));
  const { reset } = treeStorageSelectStore;

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const ref = useRef(null);

  useOutsideClick(ref, () => setOpen(false));

  useEffect(() => {
    if (!open) return;

    const calculatePosition = () => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width,
        });
      }
    };

    calculatePosition();

    window.addEventListener('scroll', calculatePosition, true);
    window.addEventListener('resize', calculatePosition);

    return () => {
      window.removeEventListener('scroll', calculatePosition, true);
      window.removeEventListener('resize', calculatePosition);
    };
  }, [open]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return (
    <div className={style.container}>
      <div className={style.containerInput} ref={ref}>
        <input
          className={style.value}
          placeholder="Выберите значение..."
          ref={ref}
          value={node?.path?.split('/')?.pop() ?? ''}
          readOnly
          onClick={() => setOpen(!open)}
        />
        <button className={style.arrowIcon} onClick={() => setOpen(!open)}>
          <img src={arrowSVG} alt="" />
        </button>
        {open && (
          <div
            className={style.treeContainer}
            style={{
              top: position.top,
              width: position.width,
            }}
          >
            <Tree
              store={treeStorageSelectStore}
              handlerLoadData={getStoragesV2}
              filterFunction={filterFunction}
              onNodeClick={(node) => {
                setNode && setNode(node);
                setOpen(false);
              }}
              defaultIcon={() => (
                <Icon
                  className={style.iconPack}
                  src={address_server_short + PUBLIC_ASSETS + 'icons/pack.svg'}
                />
              )}
              loadChildren={(nodeId, item) => getStorageChildren(nodeId)}
              nodeKeyConfig={{
                fields: ['id', 'entity'],
                separator: '_',
              }}
            />
          </div>
        )}
      </div>
      {!withoutPath && (
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'space-between',
          }}
        >
          Колонка
          <Select
            options={
              node?.structura?.columns?.map((item, index) => {
                return {
                  id: index,
                  name: item,
                };
              }) ?? []
            }
            value={column}
            onChange={(value) => setColumn && setColumn(value)}
            titleKey="name"
            clearable={false}
            valueKey="name"
            placeholder="Не выбрано"
            className={style.select}
          />
          Линия
          <Select
            options={
              node?.structura?.lines?.map((item, index) => {
                return {
                  id: index,
                  name: item,
                };
              }) ?? []
            }
            value={line}
            onChange={(value) => setLine && setLine(value)}
            titleKey="name"
            clearable={false}
            valueKey="name"
            placeholder="Не выбрано"
            className={style.select}
          />
        </div>
      )}
    </div>
  );
};

export default SelectPathStorage;
